'use client';

import { useTranslations } from 'next-intl';
import { type FormEvent, startTransition, useEffect, useState } from 'react';

import { InvisibleCaptcha, useInvisibleCaptcha } from '@/api/yandex-captcha';
import { UiLegalConsentCheckbox } from '@/ui/legal-consent-checkbox';
import { UiKitButton } from '@/ui-kit/button';
import { useDialogContext } from '@/ui-kit/drawer';
import { UiKitInput } from '@/ui-kit/input';
import { useForm } from '@/ui-tool/form';

import { loginFormAction } from '../../api/loginFormAction';
import { OauthMethods } from '../OauthMethods';

import { styles } from './LoginForm.styles';

interface LoginFormProps {
  phone?: string;
  placement: 'header' | 'cart';
  redirectUrl?: string;
  onSuccess: (phone: string) => void;
}

export function LoginForm({
  phone: initialPhone = '',
  placement,
  redirectUrl,
  onSuccess
}: LoginFormProps) {
  const t = useTranslations();
  const slots = styles();
  const { contentRef } = useDialogContext();
  const { captchaKey, captchaProps, requestToken } = useInvisibleCaptcha();
  const [isCaptchaPending, setIsCaptchaPending] = useState(false);

  const { getFieldProps, pending, state, action } = useForm(
    loginFormAction,
    {
      data: {
        phone: initialPhone
      }
    },
    { scrollRoot: () => contentRef }
  );

  useEffect(() => {
    if (state.status === 'success') {
      onSuccess(state.data?.phone as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, onSuccess]);

  const agreementFromServer =
    state.data?.agreement === true || state.data?.agreement === 'on';
  const restoreAgreement = state.status === 'error';

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;

    setIsCaptchaPending(true);

    void requestToken()
      .then((token) => {
        const formData = new FormData(form);
        formData.set('captcha', token);

        startTransition(() => {
          action(formData);
        });
      })
      .catch(() => undefined)
      .finally(() => {
        setIsCaptchaPending(false);
      });
  };

  return (
    <div>
      <h2 className={slots.title()}>{t('login.title')}</h2>
      <div className={slots.description()}>{t('login.description')}</div>
      <form onSubmit={handleSubmit}>
        <UiKitInput
          label={t('phone')}
          placeholder={t('phone')}
          mask="+7 (999) 999-99-99"
          {...getFieldProps('phone')}
        />
        <div className={slots.captcha()}>{t('login.captcha')}</div>
        <InvisibleCaptcha
          key={captchaKey}
          visible={captchaProps.visible}
          onSuccess={captchaProps.onSuccess}
          onChallengeVisible={captchaProps.onChallengeVisible}
          onChallengeHidden={captchaProps.onChallengeHidden}
          onNetworkError={captchaProps.onNetworkError}
        />
        <UiLegalConsentCheckbox
          key={
            restoreAgreement ? `agreement-${String(agreementFromServer)}` : 'agreement'
          }
          className={slots.agreement()}
          defaultChecked={restoreAgreement ? agreementFromServer : true}
          {...getFieldProps('agreement')}
        />
        <UiKitButton
          type="submit"
          color="primary"
          className={slots.button()}
          disabled={pending || isCaptchaPending}
        >
          {t('login.requestCode')}
        </UiKitButton>
      </form>
      <OauthMethods
        placement={placement}
        redirectUrl={redirectUrl}
      />
    </div>
  );
}
